import { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import Link from "next/link";
import toast from "react-hot-toast";
import { Flame, Heart, ShoppingCart } from "lucide-react";
import PropTypes from 'prop-types';
import { isProductUnavailable } from "@/lib/productAvailability";
import { getProductDescriptionSummary } from "@/lib/productDescription";
import { buildHotSaleLandingPath, buildProductPath, formatProductCategory } from "@/lib/productRoutes";
import { getProductPricing } from "@/lib/productPricing";

export default function ProductCard({ product }) {
  const [hovered, setHovered] = useState(false);
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || null);
  const [selectedVariant, setSelectedVariant] = useState(product.variants?.[0] || null);
  const [isAdding, setIsAdding] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const variantOptions = product.variants || [];
  const productPath = product.isHotSale
    ? buildHotSaleLandingPath(product, { color: selectedVariant?.name || '' })
    : buildProductPath(product, { color: selectedVariant?.name || '' });
  const displayImage = selectedVariant?.image || product.image;
  const displaySizes = product.sizes || [];
  const isFavorite = isInWishlist(product.id);
  const selectedColor = selectedVariant?.name || 'Any';
  const { salePrice, regularPrice, hasDiscount, discount } = getProductPricing(product);
  const descriptionSummary = getProductDescriptionSummary(product.description, product.name);
  const isUnavailable = isProductUnavailable(product);
  const whatsappMessage = [
    'Hi Trendy Quest!',
    '',
    `Product: ${product.name}`,
    descriptionSummary ? `Description: ${descriptionSummary}` : null,
    `Category: ${formatProductCategory(product)}`,
    `Color: ${selectedColor}`,
    displaySizes.length > 0 ? `Size: ${selectedSize || 'Any'}` : null,
    `Quantity: 1`,
    `Price: KES ${salePrice}`,
    hasDiscount ? `Regular price: KES ${regularPrice}` : null,
    hasDiscount ? `Automatic discount: ${discount}%` : null,
    '',
    'Delivery Details:',
    'Customer Name:',
    'Phone Number:',
    'Delivery Address:',
  ]
    .filter(Boolean)
    .join('\n');

  const handleAddToCart = async () => {
    if (isUnavailable) {
      toast.error('This product is currently unavailable.');
      return;
    }

    if (!selectedSize && displaySizes.length > 0) {
      toast.error("Please select a size");
      return;
    }

    setIsAdding(true);
    addToCart(product, 1, selectedSize, selectedVariant?.name || null);
    toast.success("Added to cart!");
    setTimeout(() => setIsAdding(false), 600);
  };

  return (
    <motion.article
      whileHover={{ y: -6 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-[#ebe2cf] bg-white shadow-[0_16px_40px_rgba(21,35,24,0.08)] transition-shadow duration-300 hover:shadow-[0_24px_56px_rgba(21,35,24,0.12)] sm:rounded-[1.75rem]"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#f5f2e8]">
        <Link href={productPath} className="block h-full">
          <motion.img
            src={displayImage}
            alt={product.name}
            loading="lazy"
            animate={{ scale: hovered ? 1.04 : 1 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="h-full w-full object-cover"
          />
        </Link>

        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent sm:h-24" />

        <div className="absolute left-2.5 top-2.5 sm:left-4 sm:top-4">
          <span className="rounded-full bg-white/92 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-[#24412b] shadow-sm sm:px-3 sm:py-2 sm:text-[11px] sm:tracking-[0.2em]">
            {formatProductCategory(product) || product.department || product.category || 'Collection'}
          </span>
        </div>

        {product.isHotSale ? (
          <div className="absolute left-2.5 top-11 sm:left-4 sm:top-14">
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-white shadow-sm sm:px-3 sm:py-1.5 sm:text-[11px]">
              <Flame size={12} /> Hot Sale
            </span>
          </div>
        ) : null}

        {hasDiscount ? (
          <div className="absolute bottom-2.5 left-2.5 sm:bottom-4 sm:left-4">
            <span className="rounded-full bg-[#1f6f49] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-white shadow-sm sm:px-3 sm:py-1.5 sm:text-[11px] sm:tracking-[0.16em]">
              -{discount}% off
            </span>
          </div>
        ) : null}

        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          aria-label="Toggle favorite"
          onClick={(e) => {
            e.stopPropagation();
            const wasAdded = toggleWishlist(product);
            toast.success(wasAdded ? "Saved to wishlist!" : "Removed from wishlist");
          }}
          className="absolute right-2.5 top-2.5 rounded-full bg-white p-1.5 shadow-md sm:right-4 sm:top-4 sm:p-2"
        >
          <Heart
            size={16}
            className={isFavorite ? "fill-red-500 text-red-500" : "text-gray-600"}
          />
        </motion.button>

        {isUnavailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35">
            <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 sm:px-4 sm:py-2 sm:text-sm">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 p-3 sm:p-5">
        <div className="flex min-h-[3.5rem] items-start justify-between gap-2 sm:min-h-[3.75rem] sm:gap-3">
          <Link href={productPath} className="min-w-0 flex-1">
            <h3 className="line-clamp-2 min-h-[2.5rem] font-playfair text-sm font-bold leading-5 text-[#182218] transition-colors hover:text-[#24552d] sm:min-h-[3rem] sm:text-xl sm:leading-6">
              {product.name}
            </h3>
          </Link>
          <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-[9px] font-semibold sm:px-3 sm:text-[11px] ${
            !isUnavailable ? 'bg-[#eef7ef] text-[#24552d]' : 'bg-[#f3f4f6] text-[#6b7280]'
          }`}>
            {!isUnavailable ? 'Available' : 'Unavailable'}
          </span>
        </div>

        <p className="mt-2 hidden min-h-10 line-clamp-2 text-sm leading-5 text-[#666c64] sm:mt-3 sm:block">
          {descriptionSummary || '\u00a0'}
        </p>

        <div className="mt-3 flex min-h-[3.25rem] items-center justify-between gap-2 sm:mt-4">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <p className="text-base font-playfair font-bold leading-6 text-[#1c6a43] sm:text-2xl sm:leading-7">
              KES {salePrice}
            </p>
            {hasDiscount ? (
              <p className="text-[10px] leading-4 text-[#8d9386] line-through sm:text-xs">
                KES {regularPrice}
              </p>
            ) : null}
          </div>
          {hasDiscount ? (
            <span className="shrink-0 rounded-full bg-[#eaf5ec] px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-[#1f6f49] sm:px-2.5 sm:text-[10px]">
              {discount}% off
            </span>
          ) : (
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#7b7f77] sm:text-xs">
              {displaySizes.length > 0 ? `${displaySizes.length} sizes` : ' '}
            </span>
          )}
        </div>

        {variantOptions.length > 0 && (
          <div className="mt-3 flex items-center gap-1.5 sm:mt-4 sm:gap-2">
            {variantOptions.slice(0, 4).map((variant) => (
              <button
                key={variant.name || variant.image}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedVariant(variant);
                }}
                className={`h-8 w-8 overflow-hidden rounded-xl border-2 transition-all sm:h-10 sm:w-10 sm:rounded-2xl ${
                  selectedVariant?.image === variant.image
                    ? 'border-[#24552d]'
                    : 'border-[#e5ded0] hover:border-[#ccb78c]'
                }`}
              >
                <div className="relative h-full w-full">
                  <Image
                    src={variant.image}
                    alt={variant.name || product.name}
                    fill
                    sizes="40px"
                    className="object-cover"
                  />
                </div>
              </button>
            ))}
          </div>
        )}

        {displaySizes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 sm:mt-4 sm:gap-2">
            {displaySizes.slice(0, 4).map((size) => (
              <button
                key={size}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSize(size);
                }}
                className={`rounded-full border px-2.5 py-1.5 text-[10px] font-semibold transition sm:px-3 sm:py-2 sm:text-xs ${
                  selectedSize === size
                    ? 'border-[#24552d] bg-[#24552d] text-white'
                    : 'border-[#e6dcc8] bg-[#fffdfa] text-[#536052] hover:border-[#ccb78c]'
                }`}
              >
                {size}
              </button>
            ))}
            {displaySizes.length > 4 && (
              <span className="rounded-full border border-dashed border-[#d8ccb3] px-2.5 py-1.5 text-[10px] font-semibold text-[#7a7d73] sm:px-3 sm:py-2 sm:text-xs">
                +{displaySizes.length - 4}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-2 border-t border-[#f0e8d9] bg-[#fffdfa] px-3 pb-3 pt-3 sm:gap-3 sm:px-5 sm:pb-5 sm:pt-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => {
            e.stopPropagation();
            handleAddToCart();
          }}
          disabled={isUnavailable || isAdding}
          className={`flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-all sm:py-3 sm:text-base ${
            isUnavailable
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "btn-primary"
          }`}
        >
          <motion.div
            animate={isAdding ? { rotate: 360 } : { rotate: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ShoppingCart size={18} />
          </motion.div>
          {isAdding ? "Adding..." : "Add to Cart"}
        </motion.button>

        <motion.a
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          href={`https://wa.me/254717002340?text=${encodeURIComponent(whatsappMessage)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center rounded-full border border-[#d9e8d6] bg-white py-2.5 text-sm font-semibold text-[#24552d] transition-colors hover:border-[#24552d] hover:bg-[#f4fbf4] sm:py-3 sm:text-base"
        >
          WhatsApp
        </motion.a>
      </div>
    </motion.article>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    details: PropTypes.string,
    category: PropTypes.string,
    subcategory: PropTypes.string,
    image: PropTypes.string,
    isHotSale: PropTypes.bool,
    isOutOfStock: PropTypes.bool,
    stock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.number.isRequired,
    basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    originalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sizes: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        image: PropTypes.string,
      })
    ),
  }).isRequired,
};
